/**
 * FileReviewSampling.jsx: BM-H638 (29 Sep 2026).
 *
 * File review sampling on the File Review Register, for the MLCO and COLP
 * (only the COLP can change settings or run a month). Shows the rules and
 * their policy references, previews what this month would pick and why,
 * creates the reviews, and lists past runs with their progress.
 * Server: /api/compliance/file-reviews/sampling/{settings,preview,run,runs}.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Shuffle } from "lucide-react";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";

function authHeaders() {
  const email =
    sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "";
  return email ? { "X-Bonsai-User-Id": email } : {};
}

const INPUT =
  "px-2 py-1 rounded-md text-sm bg-slate-900/60 border border-slate-700/60 text-slate-200 disabled:opacity-70";

function nameOf(staff, email) {
  return (staff || []).find((s) => s.email === email)?.name || email || "Not assigned";
}

export default function FileReviewSampling({ isColp, onRun }) {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState(null);
  const [preview, setPreview] = useState(null);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/compliance/file-reviews/sampling/settings`, { headers: authHeaders() }),
        axios.get(`${API}/compliance/file-reviews/sampling/runs`, { headers: authHeaders() }),
      ]);
      setCfg(s.data);
      setDraft(s.data.settings);
      setRuns(r.data.runs || []);
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load the sampling settings"));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy("save");
    try {
      const body = {
        aml_quarterly_percent: Number(draft.aml_quarterly_percent),
        cqs_per_fee_earner_quarter: {
          purchase: Number(draft.cqs_per_fee_earner_quarter.purchase),
          sale_or_remortgage: Number(draft.cqs_per_fee_earner_quarter.sale_or_remortgage),
        },
        reviewer_for: draft.reviewer_for || {},
      };
      await axios.put(`${API}/compliance/file-reviews/sampling/settings`, body, { headers: authHeaders() });
      toast.success("Sampling settings saved as a new version.");
      await load();
    } catch (err) {
      toast.error(errorMessage(err, "Could not save the settings"));
    } finally {
      setBusy("");
    }
  };

  const doPreview = async () => {
    setBusy("preview");
    try {
      const r = await axios.get(`${API}/compliance/file-reviews/sampling/preview`, { headers: authHeaders() });
      setPreview(r.data);
    } catch (err) {
      toast.error(errorMessage(err, "Could not preview this month's sample"));
    } finally {
      setBusy("");
    }
  };

  const doRun = async () => {
    if (!preview) return;
    if (!window.confirm(`Create ${preview.picks.length} file review(s) for ${preview.run_id}? This cannot be run twice for the same month.`)) return;
    setBusy("run");
    try {
      const r = await axios.post(`${API}/compliance/file-reviews/sampling/run`, {}, { headers: authHeaders() });
      toast.success(`${r.data.review_ids.length} file review(s) created and assigned.`);
      setPreview(null);
      await load();
      onRun && onRun();
    } catch (err) {
      toast.error(errorMessage(err, "Could not run the sampling"));
    } finally {
      setBusy("");
    }
  };

  if (error) {
    return <div className="text-sm text-red-300" data-testid="sampling-error">{error}</div>;
  }
  if (!cfg || !draft) {
    return <div className="text-xs text-slate-400">Loading sampling…</div>;
  }
  const labels = cfg.rule_labels || {};
  const routing = Object.entries(draft.reviewer_for || {});

  return (
    <section className="p-3 rounded-md border border-slate-700/60 bg-slate-900/40 space-y-3" data-testid="sampling-panel">
      <div className="flex items-center gap-2">
        <Shuffle size={16} className="text-sky-300" />
        <h2 className="text-sm font-semibold text-slate-100">Sampling</h2>
        <span className="text-[11px] text-slate-500">settings version {cfg.settings.version} ({cfg.settings.source})</span>
      </div>

      <ul className="text-xs text-slate-400 list-disc ml-4" data-testid="sampling-policy-refs">
        {Object.values(cfg.policy_refs || {}).map((p) => <li key={p}>{p}</li>)}
      </ul>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-slate-400">
        <label>AML sample each quarter (% of live matters, at least 10)
          <input type="number" min="10" max="100" disabled={!isColp} className={`${INPUT} w-24 block mt-1`}
            value={draft.aml_quarterly_percent}
            onChange={(e) => setDraft({ ...draft, aml_quarterly_percent: e.target.value })}
            data-testid="sampling-aml-percent" />
        </label>
        <label>Conveyancing purchases per fee earner per quarter
          <input type="number" min="0" disabled={!isColp} className={`${INPUT} w-24 block mt-1`}
            value={draft.cqs_per_fee_earner_quarter.purchase}
            onChange={(e) => setDraft({ ...draft, cqs_per_fee_earner_quarter: { ...draft.cqs_per_fee_earner_quarter, purchase: e.target.value } })} />
        </label>
        <label>Sales or remortgages per fee earner per quarter
          <input type="number" min="0" disabled={!isColp} className={`${INPUT} w-24 block mt-1`}
            value={draft.cqs_per_fee_earner_quarter.sale_or_remortgage}
            onChange={(e) => setDraft({ ...draft, cqs_per_fee_earner_quarter: { ...draft.cqs_per_fee_earner_quarter, sale_or_remortgage: e.target.value } })} />
        </label>
      </div>

      <div className="text-xs text-slate-400 space-y-1" data-testid="sampling-routing">
        <div>Reviewers: the COLP reviews every file except the COLP's own, which go to another director. Route a fee earner's files to someone else:</div>
        {routing.map(([fe, rv]) => (
          <div key={fe} className="flex gap-2 items-center">
            <span className="text-slate-200">{nameOf(cfg.staff, fe)}</span> →
            <span className="text-slate-200">{nameOf(cfg.staff, rv)}</span>
            {isColp && (
              <button type="button" className="text-red-300" onClick={() => {
                const next = { ...draft.reviewer_for };
                delete next[fe];
                setDraft({ ...draft, reviewer_for: next });
              }}>remove</button>
            )}
          </div>
        ))}
        {isColp && (
          <RoutingAdder staff={cfg.staff} onAdd={(fe, rv) => setDraft({ ...draft, reviewer_for: { ...(draft.reviewer_for || {}), [fe]: rv } })} />
        )}
      </div>

      <div className="text-xs text-slate-400">
        Each month is run from here. Until it is, it stays on the COLP's task list.
      </div>

      {isColp && (
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={!!busy} onClick={save}
            className="px-3 py-1.5 rounded-md text-xs bg-slate-800 text-slate-200 border border-slate-700/60 disabled:opacity-50"
            data-testid="sampling-save">Save settings</button>
          <button type="button" disabled={!!busy} onClick={doPreview}
            className="px-3 py-1.5 rounded-md text-xs bg-sky-800/40 text-sky-200 border border-sky-700/60 disabled:opacity-50"
            data-testid="sampling-preview-btn">Preview this month's sample</button>
          {busy && <Loader2 size={14} className="animate-spin text-slate-400 self-center" />}
        </div>
      )}

      {preview && (
        <div className="space-y-2" data-testid="sampling-preview">
          <div className="text-xs text-slate-300">
            {preview.run_id}: {preview.live_matters} live matters ({preview.live_definition}).
            {preview.includes_quarterly ? " First run of the quarter, so the quarterly rules apply." : " The quarterly rules have already run this quarter."}
            {" "}AML target {preview.aml_target}, already covered {preview.aml_already_covered}.
            {preview.unrated_matters > 0 && ` ${preview.unrated_matters} live matters have no risk rating.`}
            {preview.no_fee_earner > 0 && ` ${preview.no_fee_earner} have no fee earner recorded.`}
            {preview.no_matter_type > 0 && ` ${preview.no_matter_type} have no matter type recorded, so the conveyancing rule cannot consider them.`}
            {preview.conveyancing_matters && ` Residential conveyancing: ${preview.conveyancing_matters.purchase} purchases, ${preview.conveyancing_matters.sale_or_remortgage} sales or remortgages.`}
          </div>
          {preview.already_run && <div className="text-xs text-amber-300">This month has already been run.</div>}
          {preview.shortfalls.length > 0 && (
            <ul className="text-xs text-amber-300 list-disc ml-4" data-testid="sampling-shortfalls">
              {preview.shortfalls.map((s, i) => (
                <li key={i}>{labels[s.rule] || s.rule}{s.fee_earner_email ? ` for ${nameOf(cfg.staff, s.fee_earner_email)}` : ""}: wanted {s.wanted}, picked {s.picked} ({s.why}).</li>
              ))}
            </ul>
          )}
          <table className="w-full text-xs text-slate-300" data-testid="sampling-picks">
            <thead className="text-[10px] uppercase text-slate-500">
              <tr><th className="text-left p-1">Matter</th><th className="text-left p-1">Why</th><th className="text-left p-1">Risk</th><th className="text-left p-1">Fee earner</th><th className="text-left p-1">Reviewer</th></tr>
            </thead>
            <tbody>
              {preview.picks.map((p) => (
                <tr key={p.matter_ref} className="border-t border-slate-800" data-testid={`sampling-pick-${p.matter_ref}`}>
                  <td className="p-1 text-slate-100">{p.matter_ref}</td>
                  <td className="p-1">{p.reasons.map((r) => labels[r] || r).join("; ")}</td>
                  <td className="p-1">{p.band}</td>
                  <td className="p-1">{p.fee_earner_email ? nameOf(cfg.staff, p.fee_earner_email) : "Not recorded"}</td>
                  <td className={`p-1 ${p.reviewer_email ? "" : "text-amber-300"}`}>{nameOf(cfg.staff, p.reviewer_email)} <span className="text-slate-500">({p.reviewer_how})</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!preview.already_run && (
            <button type="button" disabled={!!busy || preview.picks.length === 0} onClick={doRun}
              className="px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-700/30 text-emerald-200 border border-emerald-700/60 disabled:opacity-50"
              data-testid="sampling-run-btn">Create these {preview.picks.length} reviews</button>
          )}
        </div>
      )}

      {runs.length > 0 && (
        <table className="w-full text-xs text-slate-300" data-testid="sampling-runs">
          <thead className="text-[10px] uppercase text-slate-500">
            <tr><th className="text-left p-1">Month</th><th className="text-left p-1">Picked</th><th className="text-left p-1">Progress</th><th className="text-left p-1">Shortfalls</th><th className="text-left p-1">Run by</th></tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} className="border-t border-slate-800">
                <td className="p-1">{r.id}{r.includes_quarterly ? ` (+ ${r.quarter})` : ""}</td>
                <td className="p-1">{r.picks} of {r.live_matters} live</td>
                <td className="p-1">{Object.entries(r.reviews_by_status).map(([k, v]) => `${v} ${k}`).join(", ")}</td>
                <td className="p-1">{r.shortfalls.length}</td>
                <td className="p-1">{r.created_by}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function RoutingAdder({ staff, onAdd }) {
  const [fe, setFe] = useState("");
  const [rv, setRv] = useState("");
  return (
    <div className="flex flex-wrap gap-2 items-center">
      <select className={INPUT} value={fe} onChange={(e) => setFe(e.target.value)} data-testid="sampling-route-fe">
        <option value="">Fee earner</option>
        {staff.map((s) => <option key={s.email} value={s.email}>{s.name}</option>)}
      </select>
      <span>reviewed by</span>
      <select className={INPUT} value={rv} onChange={(e) => setRv(e.target.value)} data-testid="sampling-route-rv">
        <option value="">Reviewer</option>
        {staff.filter((s) => s.email !== fe).map((s) => <option key={s.email} value={s.email}>{s.name}</option>)}
      </select>
      <button type="button" disabled={!fe || !rv} onClick={() => { onAdd(fe, rv); setFe(""); setRv(""); }}
        className="px-2 py-1 rounded-md bg-slate-800 border border-slate-700/60 disabled:opacity-50"
        data-testid="sampling-route-add">Add</button>
    </div>
  );
}
